import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, interval, map, of } from "rxjs";
import { Application } from "../../model/application";
import { ApplicationInterface } from 'src/app/model/application-interface';
import { environment } from "../../../environments/environment";
import { UserService } from '../user/user.service';
import { StorageService } from '../storage/storage.service';
import { AppConstants } from 'src/app/constants/app-constants';

/**
 * ApplicationService is used to hold/move application data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  /** A list of applications the user has access to */
  private _applications: Application[] = [];
  private _applicationsSubject = new Subject<Application[]>();
  public applications$ = this._applicationsSubject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the application.service, used to initialize services
   * 
   * @param http
   * @param authService 
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
  ) {
    // Get the applications when the token first loads
    this._userService.token$.subscribe(
      (token: string) => {
        if (token !== "") this._fetchApplications();
      });


    // Continuously query the backend to receive new applications
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetchApplications();
      });
  }

  /**
   * Approve a manual application
   * 
   * @param application The application to approve
   * @return An observable containing the success of the operation
   */
  public approveManualApplication(application: Application): Observable<boolean> {
    // Find application
    const app = this._applications.find(x => x.applicationGuid === application.applicationGuid);
    if (app == undefined) {
      throw new Error(`cannot find application with GUID ${application.applicationGuid} to approve`);
    }

    // Construct request body
    const body = {
      applicationGuid: application.applicationGuid,
      status: 'APPROVE',
    }

    return this._http.post<{status: string, message: string}>(environment.baseUrl + "/mystic/oscilar-hook", body).pipe(
      map((resp: {status: string, message: string}) => {
        // Update application model
        if (resp.status.toLowerCase() === 'success') {
          app.status = 'approved';
          this._setApplications(this._applications);
        }

        return resp.status.toLowerCase() === 'success';
      }),
    );
  }

  /**
   * Deny a manual application
   * 
   * @param application The application to deny
   * @return An observable containing the success of the operation
   */
  public denyManualApplication(application: Application): Observable<boolean> {
    // Find application
    const app = this._applications.find(x => x.applicationGuid === application.applicationGuid);
    if (app == undefined) {
      throw new Error(`cannot find application with GUID ${application.applicationGuid} to deny`);
    }

    // Construct request body
    const body = {
      applicationGuid: application.applicationGuid,
      status: 'DENY',
    }

    return this._http.post<{status: string, message: string}>(environment.baseUrl + "/mystic/oscilar-hook", body).pipe(
      map((resp: {status: string, message: string}) => {
        // Update application model
        if (resp.status.toLowerCase() === 'success') {
          app.status = 'denied';
          this._setApplications(this._applications);
        }

        return resp.status.toLowerCase() === 'success';
      }),
    );
  }

  /**
   * Get a list of applications
   * 
   * @return A reference to the current applications
   */
  public getApplications(): Application[] {
    //return this._applications.map(x => x.copyOf());
    return this._applications;
  }

  /**
   * Fetch an observable of applications
   * 
   * @return An observable containing the given applications
   */
  public fetchApplications(): Observable<Application[]> {
    // If there are present applications, return them
    if (this._applications.length !== 0) {
      return of(this._applications);
    }

    // Otherwise, create a copy of the application observable that can be piped
    return this._applicationsSubject.asObservable();
  }

  /**
   * Set a list of applications
   * 
   * @param applications
   */
  private _setApplications(applications: Application[]): void {
    this._applications = applications.map(x => x.copyOf());
    this._applicationsSubject.next(applications.map(x => x.copyOf()));
  }


  /**
   * Get applications from the backend. You should subscribe to the 'applications$'
   * field to receive updates
   */
  private _fetchApplications(): void {
    this._http.get(environment.baseUrl + "/applications").subscribe({
      next: (data: Object): void => {
        // Cast the data into a list of application interfaces
        const applicationInterfaces = data as ApplicationInterface[];
        if (applicationInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into application list`);
        }

        // Convert the interfaces to class values
        let applications: Application[] = [];
        for (let intf of applicationInterfaces) {
          applications.push(new Application(intf));
        }

        // Propagate changes if different
        this._setApplications(applications);
        console.log(`[LOG]: successfully queried applications`);
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

const arraysEqual = (a: Application[], b: Application[]): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (var i = 0; i < a.length; ++i) {
    if ((a[i] === null && a[i] !== b[i]) || !a[i].equals(b[i])) return false;
  }
  return true;
}

const delay = (delay: number) => {
  return new Promise(r => {setTimeout(r, delay)});
}